"use client";

import { useEffect, useState } from "react";

interface UseFooterProximityOptions {
	/** CSS selector for the footer element to observe. Defaults to `#main-footer`. */
	footerSelector?: string;
	/**
	 * `rootMargin` for the IntersectionObserver. A negative *bottom* value
	 * shrinks the observer's bottom edge upward, so the footer has to scroll
	 * that many pixels INTO the viewport before triggering — keeping a
	 * floating UI (filter bar / quick cart) visible until its resting zone is
	 * actually about to be covered.
	 *
	 * Default `0px 0px -120px 0px` ≈ "trigger when ~120px of the footer is
	 * visible at the bottom" — enough headroom to comfortably clear an ~80px
	 * floating dock.
	 */
	rootMargin?: string;
}

/**
 * Returns `true` once the footer has scrolled far enough into the viewport
 * that floating page chrome (filter bar, quick cart) would start overlapping
 * it. The default trigger zone leaves the dock visible while only the top
 * sliver of the footer is exposed, then hides it as the overlap approaches.
 *
 * The observed element must exist in the DOM with the given selector; the
 * default `#main-footer` is set on the global Footer component.
 */
export const useFooterProximity = ({
	footerSelector = "#main-footer",
	rootMargin = "0px 0px -120px 0px",
}: UseFooterProximityOptions = {}): boolean => {
	const [isFooterNear, setIsFooterNear] = useState(false);

	useEffect(() => {
		const el = document.querySelector(footerSelector);
		if (!el) {
			if (process.env.NODE_ENV !== "production") {
				console.warn(
					`[useFooterProximity] No element matching "${footerSelector}" — floating bars won't auto-hide near the footer.`,
				);
			}
			return;
		}
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry) setIsFooterNear(entry.isIntersecting);
			},
			{ root: null, rootMargin, threshold: 0 },
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [footerSelector, rootMargin]);

	return isFooterNear;
};
